import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { offlineDb } from '../lib/offlineDb';
import { emailAPI } from '../services/api';
import { toast } from 'sonner';

const OfflineContext = createContext(null);

export const useOffline = () => {
  const ctx = useContext(OfflineContext);
  if (!ctx) throw new Error('useOffline must be used within OfflineProvider');
  return ctx;
};

export const OfflineProvider = ({ children }) => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isSyncing, setIsSyncing] = useState(false);
  const [outboxCount, setOutboxCount] = useState(0);
  const wasOfflineRef = useRef(false);
  // Bug fix: use a ref so the online/offline handler always has the latest flushOutbox
  // without needing to re-register event listeners on every render.
  const flushOutboxRef = useRef(null);

  // Listen for online/offline events
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      if (wasOfflineRef.current) {
        toast.success('Connexion rétablie', { description: 'Synchronisation en cours...' });
        // Call via ref to always use the latest version without stale closure
        flushOutboxRef.current?.();
      }
    };

    const handleOffline = () => {
      setIsOnline(false);
      wasOfflineRef.current = true;
      toast.warning('Vous êtes hors-ligne', {
        description: 'Vos données en cache sont disponibles. Les emails en attente seront envoyés au retour du réseau.',
        duration: 5000,
      });
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Load initial outbox count
    loadOutboxCount();

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadOutboxCount = useCallback(async () => {
    try {
      const items = await offlineDb.getOutbox();
      setOutboxCount(items.length);
    } catch {
      setOutboxCount(0);
    }
  }, []);

  // Cache API data for offline use
  const cacheEmails = useCallback(async (emails) => {
    try {
      await offlineDb.cacheEmails(emails);
    } catch (err) {
      console.warn('Failed to cache emails:', err);
    }
  }, []);

  const cacheContacts = useCallback(async (contacts) => {
    try {
      await offlineDb.cacheContacts(contacts);
    } catch (err) {
      console.warn('Failed to cache contacts:', err);
    }
  }, []);

  const cacheEvents = useCallback(async (events) => {
    try {
      await offlineDb.cacheEvents(events);
    } catch (err) {
      console.warn('Failed to cache events:', err);
    }
  }, []);

  // Get cached data when offline
  const getCachedEmails = useCallback(async (folder) => {
    try {
      return await offlineDb.getCachedEmailsByFolder(folder);
    } catch {
      return [];
    }
  }, []);

  const getCachedContacts = useCallback(async () => {
    try {
      return await offlineDb.getCachedContacts();
    } catch {
      return [];
    }
  }, []);

  const getCachedEvents = useCallback(async () => {
    try {
      return await offlineDb.getCachedEvents();
    } catch {
      return [];
    }
  }, []);

  // Save draft locally
  const saveDraftOffline = useCallback(async (draft) => {
    try {
      await offlineDb.saveDraft(draft);
      toast.info('Brouillon sauvegardé localement');
    } catch (err) {
      console.warn('Failed to save draft:', err);
    }
  }, []);

  // Queue email for sending when back online
  const queueEmail = useCallback(async (emailData) => {
    try {
      await offlineDb.addToOutbox(emailData);
      await loadOutboxCount();
      toast.info('Email mis en file d\'attente', {
        description: 'Il sera envoyé automatiquement au retour du réseau.',
      });
    } catch (err) {
      console.warn('Failed to queue email:', err);
    }
  }, [loadOutboxCount]);

  // Flush outbox: send all queued emails
  const flushOutbox = useCallback(async () => {
    const items = await offlineDb.getOutbox();
    if (items.length === 0) return;

    setIsSyncing(true);
    let sent = 0;
    let failed = 0;

    for (const item of items) {
      try {
        await emailAPI.sendEmail({
          to: item.to,
          cc: item.cc || [],
          bcc: item.bcc || [],
          subject: item.subject,
          body_html: item.body_html,
          body_text: item.body_text || '',
          signature_id: item.signature_id || null,
          attachments: item.attachments || [],
        });
        await offlineDb.removeFromOutbox(item.id);
        sent++;
      } catch (err) {
        console.error('Failed to send queued email:', err);
        failed++;
      }
    }

    await loadOutboxCount();
    setIsSyncing(false);

    if (sent > 0) {
      toast.success(`${sent} email(s) en attente envoyé(s)`);
    }
    if (failed > 0) {
      toast.error(`${failed} email(s) n'ont pas pu être envoyés`);
    }
  }, [loadOutboxCount]);

  // Bug fix: keep ref in sync with the latest flushOutbox so the online handler
  // (registered once in useEffect) always calls the up-to-date version.
  useEffect(() => {
    flushOutboxRef.current = flushOutbox;
  }, [flushOutbox]);

  const value = {
    isOnline,
    isSyncing,
    outboxCount,
    cacheEmails,
    cacheContacts,
    cacheEvents,
    getCachedEmails,
    getCachedContacts,
    getCachedEvents,
    saveDraftOffline,
    queueEmail,
    flushOutbox,
    loadOutboxCount,
  };

  return (
    <OfflineContext.Provider value={value}>
      {children}
    </OfflineContext.Provider>
  );
};
